// src/admin/components/VariantManagement.tsx
import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Drawer,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Pagination,
  InputAdornment,
  Tooltip,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Backdrop,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  AddBox as AddBoxIcon,
  Search as SearchIcon,
  Delete,
  Close as CloseIcon,
} from '@mui/icons-material';
import StackedLineChartIcon from '@mui/icons-material/StackedLineChart';
import { VariantType, VariantOption, VariantTypeFormData } from '../../../enums/product';
import { useAuth } from '../../../contexts/AuthContext';
import { useVariantTypesPaginated, useVariantTypeStats, useVariantMutations } from '../../../hooks/useVariants';
import ToastNotification from '../common/Toastnotification';

const VARIANT_TYPES_PER_PAGE = 10;

interface ToastState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

// ── Loading overlay label type ────────────────────────────────────────────────
type OverlayMode = 'save-add' | 'save-edit' | 'delete' | 'toggle' | null;

const getSoftChipSx = (color: string, isDark: boolean = false) => {
  type PaletteEntry = { bg: string; border: string; text: string; iconColor: string };

  const lightPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.12)', border: 'rgba(255,167,38,0.4)', text: '#b76e00', iconColor: '#e68900' },
    info: { bg: 'rgba(41,182,246,0.12)', border: 'rgba(41,182,246,0.4)', text: '#0277bd', iconColor: '#039be5' },
    primary: { bg: 'rgba(33,150,243,0.12)', border: 'rgba(33,150,243,0.4)', text: '#1565c0', iconColor: '#1976d2' },
    success: { bg: 'rgba(102,187,106,0.15)', border: 'rgba(102,187,106,0.45)', text: '#2e7d32', iconColor: '#43a047' },
    error: { bg: 'rgba(239,83,80,0.12)', border: 'rgba(239,83,80,0.4)', text: '#c62828', iconColor: '#e53935' },
    secondary: { bg: 'rgba(171,71,188,0.10)', border: 'rgba(171,71,188,0.35)', text: '#6a1b9a', iconColor: '#8e24aa' },
    default: { bg: 'rgba(158,158,158,0.12)', border: 'rgba(158,158,158,0.4)', text: '#424242', iconColor: '#757575' },
  };

  const darkPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.22)', border: 'rgba(255,167,38,0.55)', text: '#ffcc80', iconColor: '#ffa726' },
    info: { bg: 'rgba(41,182,246,0.20)', border: 'rgba(41,182,246,0.50)', text: '#81d4fa', iconColor: '#29b6f6' },
    primary: { bg: 'rgba(33,150,243,0.22)', border: 'rgba(33,150,243,0.50)', text: '#90caf9', iconColor: '#42a5f5' },
    success: { bg: 'rgba(102,187,106,0.22)', border: 'rgba(102,187,106,0.50)', text: '#a5d6a7', iconColor: '#66bb6a' },
    error: { bg: 'rgba(239,83,80,0.22)', border: 'rgba(239,83,80,0.50)', text: '#ef9a9a', iconColor: '#ef5350' },
    secondary: { bg: 'rgba(171,71,188,0.22)', border: 'rgba(171,71,188,0.50)', text: '#ce93d8', iconColor: '#ab47bc' },
    default: { bg: 'rgba(158,158,158,0.22)', border: 'rgba(158,158,158,0.50)', text: '#e0e0e0', iconColor: '#bdbdbd' },
  };

  const palette = isDark ? darkPalette : lightPalette;
  const p = palette[color] ?? palette.default;

  return {
    backgroundColor: p.bg,
    borderColor: p.border,
    color: p.text,
    fontWeight: 600,
    border: `1px solid ${p.border}`,
    '& .MuiChip-icon': { color: p.iconColor },
    '& .MuiChip-label': { color: p.text },
  };
};

interface LoadingOverlayProps {
  wrapperRef: React.RefObject<HTMLDivElement | null>;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ wrapperRef }) => {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const theme = useTheme();

  const overlayBg = theme.palette.mode === 'dark'
    ? 'rgba(26, 29, 36, 0.85)'
    : 'rgba(255, 255, 255, 0.85)';

  React.useEffect(() => {
    const measure = () => {
      if (wrapperRef.current) setRect(wrapperRef.current.getBoundingClientRect());
    };
    measure();
    const el = wrapperRef.current;
    window.addEventListener('resize', measure);
    el?.addEventListener('scroll', measure);
    return () => {
      window.removeEventListener('resize', measure);
      el?.removeEventListener('scroll', measure);
    };
  }, [wrapperRef]);

  if (!rect) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: rect.top, left: rect.left,
        width: rect.width, height: rect.height,
        backgroundColor: overlayBg,
        backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1100, pointerEvents: 'all', borderRadius: 'inherit',
      }}
    >
      <Box sx={{ textAlign: 'center' }}>
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading variant types...
        </Typography>
      </Box>
    </Box>
  );
};

const VariantManagement: React.FC = () => {
  const { currentUser, hasPermission, isSuperAdmin } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDark = theme.palette.mode === 'dark';

  // ── UI state ──────────────────────────────────────────────────────────────
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'add' | 'edit'>('add');
  const [selectedVariantType, setSelectedVariantType] = useState<VariantType | null>(null);
  const [formData, setFormData] = useState<VariantTypeFormData>({ name: '', isActive: true, options: [] });
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<string>('');
  const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'info' });
  const [deleteTarget, setDeleteTarget] = useState<VariantType | null>(null);
  const [deleteDialogLoading, setDeleteDialogLoading] = useState(false);

  // ── Full-screen overlay mode ──────────────────────────────────────────────
  // null = hidden; 'save-add' | 'save-edit' | 'delete' | 'toggle' = visible
  const [overlayMode, setOverlayMode] = useState<OverlayMode>(null);

  // ── Pagination & filter state ─────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const optionsContainerRef = useRef<HTMLDivElement>(null);
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  // ── React Query hooks ─────────────────────────────────────────────────────
  const {
    data: variantData,
    isLoading: variantLoading,
    isFetching: variantIsFetching,
    isError: variantError,
    error: variantErrorObj,
  } = useVariantTypesPaginated({
    page: currentPage,
    search: searchQuery,
    pageSize: VARIANT_TYPES_PER_PAGE,
  });

  const { data: statsData } = useVariantTypeStats();
  const { create, update, remove, toggle } = useVariantMutations();

  const canView = isSuperAdmin || hasPermission('variant.view');
  const canCreate = isSuperAdmin || hasPermission('variant.create');
  const canEdit = isSuperAdmin || hasPermission('variant.edit');
  const canDelete = isSuperAdmin || hasPermission('variant.delete');
  // toggle status maps to edit — it's an inline field update
  const canToggle = isSuperAdmin || hasPermission('variant.edit');

  // ── Derived values ────────────────────────────────────────────────────────
  const variantTypes = variantData?.variantTypes ?? [];
  const totalVariantTypes = variantData?.totalCount ?? 0;
  const totalPages = Math.ceil(totalVariantTypes / VARIANT_TYPES_PER_PAGE);
  const stats = statsData ?? { total: 0, active: 0, inactive: 0, totalOptions: 0 };
  const loading = variantLoading || variantIsFetching;

  // Auto-scroll to bottom of options list when a new option is added
  React.useEffect(() => {
    if (optionsContainerRef.current && formData.options.length > 0) {
      optionsContainerRef.current.scrollTo({
        top: optionsContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [formData.options.length]);

  // ── Toast ─────────────────────────────────────────────────────────────────
  const showToast = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setToast({ open: true, message, severity });
  };
  const handleCloseToast = () => setToast(t => ({ ...t, open: false }));

  // ── Reset to page 1 ───────────────────────────────────────────────────────
  const resetToFirstPage = () => {
    setCurrentPage(1);
  };

  // ── Pagination handler ────────────────────────────────────────────────────
  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  // ── Search ────────────────────────────────────────────────────────────────
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearchQuery(searchInput);
      resetToFirstPage();
    }
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    resetToFirstPage();
    showToast('Search cleared', 'info');
  };

  // ── Form helpers ──────────────────────────────────────────────────────────
  const resetForm = () => {
    setFormData({ name: '', isActive: true, options: [] });
    setSelectedVariantType(null);
  };

  const handleAddVariantType = () => { resetForm(); setDrawerMode('add'); setDrawerOpen(true); };

  const handleEditVariantType = (variantType: VariantType) => {
    setFormData({ name: variantType.name, isActive: variantType.isActive, options: variantType.options || [] });
    setSelectedVariantType(variantType);
    setDrawerMode('edit');
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => { setDrawerOpen(false); resetForm(); };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteVariantType = (variantType: VariantType) => setDeleteTarget(variantType);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteDialogLoading(true);
      setDeleteLoading(deleteTarget.id!);
      // Close the confirm dialog immediately and show the full-screen overlay
      setDeleteTarget(null);
      setOverlayMode('delete');
      const result = await remove.mutateAsync(deleteTarget.id!);
      if (result.success) {
        resetToFirstPage();
        showToast('Variant type deleted successfully', 'success');
      } else {
        showToast(result.message || 'Failed to delete variant type', 'error');
      }
    } catch {
      showToast('Failed to delete variant type', 'error');
    } finally {
      setDeleteDialogLoading(false);
      setDeleteLoading('');
      setOverlayMode(null);
    }
  };

  const handleDeleteCancel = () => setDeleteTarget(null);

  // ── Toggle status ─────────────────────────────────────────────────────────
  const handleToggleStatus = async (variantType: VariantType) => {
    try {
      setOverlayMode('toggle');
      const newStatus = !variantType.isActive;
      const result = await toggle.mutateAsync({ variantTypeId: variantType.id!, isActive: newStatus });
      if (result.success) {
        resetToFirstPage();
        showToast(`Variant type ${newStatus ? 'activated' : 'deactivated'} successfully`, 'success');
      } else {
        showToast(result.message || 'Failed to update variant type status', 'error');
      }
    } catch {
      showToast('Failed to update variant type status', 'error');
    } finally {
      setOverlayMode(null);
    }
  };

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <StackedLineChartIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2, opacity: 0.7 }} />
            <Typography variant="h5" gutterBottom>Access Denied</Typography>
            <Typography variant="body1" color="text.secondary">
              You don't have permission to view variant types. Please contact your administrator.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // ── Form submit ───────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) { showToast('Variant type name is required', 'error'); return; }
    if (formData.options.length === 0) { showToast('At least one variant option is required', 'error'); return; }
    if (formData.options.some(o => !o.name.trim())) { showToast('All variant options must have a name', 'error'); return; }

    setSaveLoading(true);
    setOverlayMode(drawerMode === 'add' ? 'save-add' : 'save-edit');
    try {
      let result;
      if (drawerMode === 'add') {
        if (!currentUser) { showToast('You must be logged in', 'error'); return; }
        result = await create.mutateAsync({ formData, userId: currentUser.uid });
        if (result.success) showToast('Variant type created successfully', 'success');
        else showToast(result.message || 'Failed to create variant type', 'error');
      } else if (selectedVariantType) {
        result = await update.mutateAsync({ variantTypeId: selectedVariantType.id!, formData });
        if (result.success) showToast('Variant type updated successfully', 'success');
        else showToast(result.message || 'Failed to update variant type', 'error');
      }
      if (result?.success) {
        setDrawerOpen(false);
        resetForm();
        resetToFirstPage();
      }
    } catch {
      showToast('Failed to save variant type', 'error');
    } finally {
      setSaveLoading(false);
      setOverlayMode(null);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleAddOption = () => {
    setFormData(prev => ({ ...prev, options: [...prev.options, { name: '', value: '', isActive: true }] }));
  };

  const handleUpdateOption = (index: number, field: keyof VariantOption, value: any) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.map((option, i) => i === index ? { ...option, [field]: value } : option),
    }));
  };

  const handleRemoveOption = (index: number) => {
    setFormData(prev => ({ ...prev, options: prev.options.filter((_, i) => i !== index) }));
  };

  const handleRefresh = () => {
    resetToFirstPage();
    showToast('Variant types refreshed', 'info');
  };

  // ── Overlay label helpers ─────────────────────────────────────────────────
  const getOverlayTitle = (): string => {
    switch (overlayMode) {
      case 'save-add': return 'Creating Variant Type…';
      case 'save-edit': return 'Updating Variant Type…';
      case 'delete': return 'Deleting Variant Type…';
      case 'toggle': return 'Updating Status…';
      default: return 'Please wait…';
    }
  };

  // ── Initial full-page loading ─────────────────────────────────────────────
  if (loading && variantTypes.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ ml: 2 }}>Loading variant types...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: { md: '100%' },
        display: 'flex',
        flexDirection: 'column',
        overflow: { xs: 'visible', md: 'hidden' },
        p: { xs: 2, sm: 3 },
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <StackedLineChartIcon sx={{ fontSize: { xs: 28, md: 32 }, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
                Variant Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage product variants like size, color, and material options
              </Typography>
            </Box>
          </Stack>
          {canCreate && (
            <Button
              onClick={handleAddVariantType}
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}
            >
              Add Variant Type
            </Button>
          )}
        </Stack>

        {/* ── Stats Cards ── */}
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: 3 }} alignItems="stretch">
          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>Variant Types</Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
                  {stats.total}
                </Typography>
                <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', flexShrink: 0 }} />
                    <Typography variant="caption" color="success.main" noWrap>{stats.active} Active</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'text.disabled', flexShrink: 0 }} />
                    <Typography variant="caption" color="text.secondary" noWrap>{stats.inactive} Inactive</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>Total Options</Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
                  {stats.totalOptions}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {stats.total > 0 ? `avg ${(stats.totalOptions / stats.total).toFixed(1)} per type` : '—'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>Active Types</Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
                  {stats.active}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {stats.total > 0 ? `${((stats.active / stats.total) * 100).toFixed(1)}% of total` : '—'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>Inactive Types</Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
                  {stats.inactive}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {stats.total > 0 ? `${((stats.inactive / stats.total) * 100).toFixed(1)}% suspended` : '—'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ── Search ── */}
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
            <TextField
              placeholder="Search by variant type name, options... (Press Enter)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              sx={{ flexGrow: 1, maxWidth: { sm: 500 } }}
              size="small"
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
              }}
            />
            {searchQuery && (
              <Button onClick={clearSearch} variant="outlined" size="small" startIcon={<Delete />}>
                Clear Search
              </Button>
            )}
          </Stack>
          {searchQuery && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Found {variantTypes.length} variant types matching "{searchQuery}"
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>

      {/* ── Scrollable Body ── */}
      <Box sx={{ flex: 1, overflow: { xs: 'visible', md: 'auto' }, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>

        {variantTypes.length === 0 && !loading && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            <Card sx={{ width: '100%', maxWidth: 500 }}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <SettingsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  {searchQuery ? 'No Variant Types Match Your Search' : 'No Variant Types Found'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                  {searchQuery
                    ? 'Try adjusting your search query.'
                    : "You haven't created any variant types yet. Start by adding your first variant type to manage product options like sizes, colors, or materials."}
                </Typography>
                {searchQuery ? (
                  <Button onClick={clearSearch} variant="outlined">Clear Search</Button>
                ) : canCreate && (
                  <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddVariantType}>
                    Add Variant Type
                  </Button>
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {variantTypes.length > 0 && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>
            <TableContainer
              component={Paper}
              ref={tableWrapperRef}
              sx={{
                flex: 1,
                overflow: { xs: 'auto', md: 'auto' },
                maxHeight: { md: '100%' },
                mb: 2,
                position: 'relative',
                '& .MuiTable-root': { minWidth: { xs: 650, md: 800 } },
              }}
            >
              <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell>Variant Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Options</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Total Options</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Created At</TableCell>
                    {(canEdit || canToggle || canDelete) && (
                      <TableCell align="right">Actions</TableCell>
                    )}                  </TableRow>
                </TableHead>
                <TableBody>
                  {variantTypes.map((variantType) => (
                    <TableRow
                      key={variantType.id}
                      hover
                      sx={{
                        '&:hover': { backgroundColor: 'action.hover' },
                        '&:last-child td, &:last-child th': { borderBottom: 0 },
                      }}
                    >
                      <TableCell>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                          {variantType.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' } }}>
                          {variantType.options?.length || 0} option{(variantType.options?.length || 0) !== 1 ? 's' : ''}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                          <Chip
                            label={variantType.isActive ? 'Active' : 'Inactive'}
                            size="small"
                            variant="outlined"
                            sx={getSoftChipSx(variantType.isActive ? 'success' : 'default', isDark)}
                          />
                          {variantType.isRequired && (
                            <Chip
                              label="Required"
                              size="small"
                              variant="outlined"
                              sx={{ ...getSoftChipSx('warning', isDark), display: { xs: 'none', sm: 'flex' } }}
                            />
                          )}
                          {variantType.allowsMultiple && (
                            <Chip
                              label="Multiple"
                              size="small"
                              variant="outlined"
                              sx={{ ...getSoftChipSx('info', isDark), display: { xs: 'none', sm: 'flex' } }}
                            />
                          )}
                        </Stack>
                      </TableCell>

                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Box sx={{ maxWidth: 300 }}>
                          {variantType.options && variantType.options.length > 0 ? (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                              {variantType.options.slice(0, 5).map((option, index) => (
                                <Chip
                                  key={option.id || index}
                                  label={option.name}
                                  size="small"
                                  variant="outlined"
                                  sx={getSoftChipSx(option.isActive ? 'default' : 'error', isDark)}
                                />
                              ))}
                              {variantType.options.length > 5 && (
                                <Chip
                                  label={`+${variantType.options.length - 5} more`}
                                  size="small"
                                  variant="outlined"
                                  sx={getSoftChipSx('primary', isDark)}
                                />
                              )}
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">No options</Typography>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2">{variantType.options?.length || 0}</Typography>
                      </TableCell>

                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2" color="text.secondary">
                          {variantType.createdAt
                            ? new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' }).format(variantType.createdAt)
                            : '-'}
                        </Typography>
                      </TableCell>

                      {(canEdit || canToggle || canDelete) && (
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="nowrap">
                            {canEdit && (
                              <Tooltip title="Edit">
                                <IconButton onClick={() => handleEditVariantType(variantType)} size="small" color="primary">
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canToggle && (
                              <Tooltip title={variantType.isActive ? 'Deactivate' : 'Activate'}>
                                <IconButton onClick={() => handleToggleStatus(variantType)} size="small" color="info">
                                  {variantType.isActive ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            )}
                            {canDelete && (
                              <Tooltip title="Delete">
                                <IconButton
                                  onClick={() => handleDeleteVariantType(variantType)}
                                  disabled={deleteLoading === variantType.id}
                                  size="small" color="error"
                                >
                                  {deleteLoading === variantType.id
                                    ? <CircularProgress size={16} />
                                    : <DeleteIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {loading && <LoadingOverlay wrapperRef={tableWrapperRef} />}

            <Paper elevation={2} sx={{ flexShrink: 0, borderRadius: 1 }}>
              <Box sx={{ py: 2, px: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 3 }}
                  alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Showing {totalVariantTypes === 0 ? 0 : (currentPage - 1) * VARIANT_TYPES_PER_PAGE + 1}–
                    {Math.min(currentPage * VARIANT_TYPES_PER_PAGE, totalVariantTypes)} of {totalVariantTypes} variant types
                  </Typography>
                  <Pagination
                    count={Math.max(1, totalPages)} page={currentPage} onChange={handlePageChange}
                    color="primary" size={isMobile ? 'small' : 'medium'} showFirstButton showLastButton
                    disabled={loading || totalPages <= 1}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Page {currentPage} of {Math.max(1, totalPages)}
                  </Typography>
                </Stack>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      {/* ── Drawer ── */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{ sx: { width: { xs: '100%', sm: 560 } } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{
            px: 3, py: 2, borderBottom: 1, borderColor: 'divider',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
          }}>
            <Typography variant="h6">
              {drawerMode === 'add' ? 'Add New Variant Type' : 'Edit Variant Type'}
            </Typography>
            <IconButton onClick={handleCloseDrawer} edge="end"><CloseIcon /></IconButton>
          </Box>

          <Box component="form" onSubmit={handleSubmit} sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
            <Box sx={{ px: 3, pt: 3, flexShrink: 0 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth label="Variant Type Name" name="name"
                    value={formData.name} onChange={handleInputChange}
                    required placeholder="e.g., Size, Color, Material"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Switch name="isActive" checked={formData.isActive} onChange={handleInputChange} />}
                    label="Active"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ mt: 3, mb: 0 }} />

              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2.5, mb: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={600}>Variant Options</Typography>
                <Button onClick={handleAddOption} startIcon={<AddBoxIcon />} variant="outlined" size="small">
                  Add Option
                </Button>
              </Stack>
            </Box>

            <Box
              ref={optionsContainerRef}
              sx={{
                flex: 1, overflow: 'auto', px: 3, pb: 3, minHeight: 0,
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.2)', borderRadius: '3px' },
                '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(0,0,0,0.35)' },
              }}
            >
              {formData.options.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4, border: '2px dashed', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    No options added yet. Click "Add Option" to get started.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1.5}>
                  {formData.options.map((option, index) => (
                    <Paper key={index} variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth label="Option Name" value={option.name}
                            onChange={(e) => handleUpdateOption(index, 'name', e.target.value)}
                            placeholder="e.g., Red, Large" size="small"
                            autoFocus={index === formData.options.length - 1}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth label="Value (Optional)" value={option.value || ''}
                            onChange={(e) => handleUpdateOption(index, 'value', e.target.value)}
                            placeholder="e.g., #FF0000, XL" size="small"
                          />
                        </Grid>
                        <Grid item xs={8} sm={2}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={option.isActive}
                                onChange={(e) => handleUpdateOption(index, 'isActive', e.target.checked)}
                                size="small"
                              />
                            }
                            label="Active"
                            sx={{ mr: 0 }}
                          />
                        </Grid>
                        <Grid item xs={4} sm={2} sx={{ display: 'flex', justifyContent: { xs: 'flex-end', sm: 'center' } }}>
                          <Tooltip title="Remove Option">
                            <IconButton onClick={() => handleRemoveOption(index)} color="error" size="small">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Box>

            <Divider />
            <Box sx={{ px: 3, py: 2, display: 'flex', gap: 2, justifyContent: 'flex-end', flexShrink: 0 }}>
              <Button onClick={handleCloseDrawer} disabled={saveLoading} startIcon={<CancelIcon />}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={saveLoading}
                startIcon={saveLoading ? <CircularProgress size={16} /> : <SaveIcon />}>
                {drawerMode === 'add' ? 'Create Variant Type' : 'Update Variant Type'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog open={!!deleteTarget} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Variant Type</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{' '}
            <strong>"{deleteTarget?.name}"</strong> variant type?{' '}
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} disabled={deleteDialogLoading}>Cancel</Button>
          <Button
            variant="contained" color="error" onClick={handleDeleteConfirm}
            disabled={deleteDialogLoading}
            startIcon={deleteDialogLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Full-screen operation overlay (mirrors EnhancedProductDrawer pattern) ── */}
      <Backdrop
        open={overlayMode !== null}
        sx={{ zIndex: (t) => t.zIndex.drawer + 200, flexDirection: 'column', gap: 3, bgcolor: 'rgba(0,0,0,0.6)' }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            px: 5,
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2.5,
            minWidth: 280,
            boxShadow: 24,
          }}
        >
          <CircularProgress size={48} thickness={4} />
          <Box sx={{ width: '100%' }}>
            <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}>
              {getOverlayTitle()}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
              Please wait, do not close this window
            </Typography>
          </Box>
        </Box>
      </Backdrop>

      <ToastNotification open={toast.open} message={toast.message} severity={toast.severity} onClose={handleCloseToast} />
    </Box>
  );
};

export default VariantManagement;